"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import type { BaseEntity } from "../types";
import type { HorizontalCardViewProps, EditingState } from "./types";
import { useCardEditing, useCardActions, useCardFields } from "./hooks";
import {
  HorizontalCard,
  CardHeaderSection,
  CardContentSection,
} from "./components";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

/**
 * HorizontalCardView - Displays entities as a grid of cards with infinite scroll
 * Reuses field rendering and action systems from table
 *
 * Features:
 * - 4-column responsive grid layout
 * - Infinite scroll pagination (loads next page when scrolling near bottom)
 * - Inline editing support
 * - Selection/checkboxes
 *
 * Organized into smaller, manageable files:
 * - hooks/: useCardEditing, useCardActions, useCardFields
 * - components/: EditableField, CardActionMenu, CardHeaderSection, CardContentSection, HorizontalCard
 * - types.ts: Type definitions
 */
export const HorizontalCardView = React.memo(
  function HorizontalCardView<TData extends BaseEntity>({
    config,
    data,
    visibleColumns,
    onView,
    onEdit,
    onInlineEdit,
    onDelete,
    appUser,
    selectedRows = new Set(),
    onSelectionChange,
    onOpenDetailModal,
    hooks,
    hasNextPage = false,
    isFetchingNextPage = false,
    onLoadMore,
    inlineEditingDisabled = false,
  }: HorizontalCardViewProps<TData>) {
    // ========================================================================
    // STATE
    // ========================================================================

    const [editingCell, setEditingCell] = useState<EditingState | null>(null);
    const [editValue, setEditValue] = useState<string>("");
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Throttle guard: state updates (isFetchingNextPage) are async, so a raw
    // scroll handler can fire onLoadMore many times before it flips, skipping
    // pages and thrashing renders. Mirror the desktop infinite-scroll debounce.
    const lastLoadTimeRef = useRef(0);

    // ========================================================================
    // HOOKS
    // ========================================================================

    const { handleCellEdit, handleCellSave, handleCellCancel } = useCardEditing({
      data,
      onInlineEdit,
      editValue,
      setEditingCell,
      setEditValue,
    });

    const { handleSelectRow, handleCardClick } = useCardActions({
      data,
      selectedRows,
      onSelectionChange,
      onOpenDetailModal,
      onView,
    });

    const { fieldLayout, renderFieldValue } = useCardFields({
      config,
      visibleColumns,
    });

    // ========================================================================
    // INFINITE SCROLL HANDLER
    // ========================================================================

    const handleScroll = useCallback(() => {
      if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage || !onLoadMore) {
        return;
      }

      const container = scrollContainerRef.current;
      const { scrollTop, clientHeight, scrollHeight } = container;

      // Calculate how far from the bottom we are
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // Trigger load more when within 200px of the bottom, debounced so a burst
      // of scroll events can't fire onLoadMore repeatedly before
      // isFetchingNextPage flips true.
      if (distanceFromBottom < 200) {
        const now = Date.now();
        if (now - lastLoadTimeRef.current > 500) {
          lastLoadTimeRef.current = now;
          onLoadMore();
        }
      }
    }, [hasNextPage, isFetchingNextPage, onLoadMore]);

    // ========================================================================
    // SCROLL EVENT LISTENER
    // ========================================================================

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    // ========================================================================
    // RENDER
    // ========================================================================

    if (data.length === 0 && !isFetchingNextPage) {
      return (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          No items to display
        </div>
      );
    }

    return (
      <div className="w-full">
        {/* Grid container with vertical scrolling */}
        <div
          ref={scrollContainerRef}
          className="w-full max-h-[min(720px,calc(100vh-15rem))] overflow-y-auto overflow-x-hidden [transform:translateZ(0)] [will-change:scroll-position]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-5 px-4 py-4 md:px-6 lg:px-8">
            {data.map((item, index) => (
              <HorizontalCard
                key={item.id}
                item={item}
                index={index}
                onCardClick={handleCardClick}
                config={config}
                hooks={hooks}
              >
                <CardHeaderSection
                  item={item}
                  config={config}
                  hooks={hooks}
                  selectedRows={selectedRows}
                  primaryField={fieldLayout.primaryField}
                  secondaryField={fieldLayout.secondaryField}
                  editingCell={editingCell}
                  editValue={editValue}
                  onSelectRow={handleSelectRow}
                  onEditChange={setEditValue}
                  onEdit={handleCellEdit}
                  onSave={handleCellSave}
                  onCancel={handleCellCancel}
                  renderValue={renderFieldValue}
                  inlineEditingDisabled={inlineEditingDisabled}
                />
                <CardContentSection
                  item={item}
                  contentFields={fieldLayout.contentFields}
                  config={config}
                  editingCell={editingCell}
                  editValue={editValue}
                  onEditChange={setEditValue}
                  onEdit={handleCellEdit}
                  onSave={handleCellSave}
                  onCancel={handleCellCancel}
                  renderValue={renderFieldValue}
                  inlineEditingDisabled={inlineEditingDisabled}
                />
              </HorizontalCard>
            ))}
          </div>

          {/* Loading indicator at bottom while fetching next page */}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          )}

          {/* End of results message */}
          {!hasNextPage && data.length > 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              You've reached the end of results
            </div>
          )}
        </div>
      </div>
    );
  }
) as <TData extends BaseEntity>(props: HorizontalCardViewProps<TData>) => React.ReactElement;
